import type { CustomFile } from '../types';
import type { TypeExtractZipConfig, TypeZipEntry } from './types';

import { merge } from '../../../helper';

/*
 * Straight at the module, not through the utils/files barrel: that barrel
 * re-exports the file demo, which reaches back into the upload UI, so naming an
 * extracted entry would drag the dropzone and the form prototypes in with it.
 */
import getPath from '../../files/getPath';
import runWithConcurrency from '../../async/runWithConcurrency';

import readEntries, { normalizePath } from './readEntries';

// ----------------------------------------------------------------------------

const SIGNATURE_LOCAL = 0x04034b50;
const SIZE_LOCAL = 30;

const METHOD_STORED = 0;
const METHOD_DEFLATE = 8;

// ----------------------------------------------------------------------------

/*
 * Opens a zip and returns the files inside it, ready to be uploaded like any other file list.
 * Entries that cannot be read on their own (directories, encrypted, unsupported compression method,
 * paths escaping the root) are warned about and left out.
 *
 * Every entry is decompressed before this resolves, so peak memory is the archive's full
 * uncompressed size. Stored entries stay cheap — the returned blob is a view on the source — but
 * deflated ones are materialized. There is no streaming path: for an archive larger than the tab
 * can hold, `readEntries` at least lists it without decompressing anything.
 */
async function extract(blob: Blob, config: TypeExtractZipConfig = {}): Promise<CustomFile[]> {
    const defConfig: TypeExtractZipConfig = {
        rootFolder: true,
        concurrency: 8,
    };
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const entries = (await readEntries(blob)).filter((entry): boolean => isReadable(entry));
    const rootFolder = getRootFolder(blob, defConfig.rootFolder);

    const files: (CustomFile | undefined)[] = new Array(entries.length);

    await runWithConcurrency(
        entries,
        getConcurrency(defConfig.concurrency),
        async (entry, index): Promise<void> => {
            files[index] = await extractEntry(blob, entry, rootFolder);
        },
        {
            onError: (err, entry): void => {
                console.warn(`Skipped zip entry "${entry.path}":`, err);
            },
        },
    );

    // ------------------------------------------------------------------------

    return files.filter((file): file is CustomFile => file !== undefined);
}

// ----------------------------------------------------------------------------

function isReadable(entry: TypeZipEntry): boolean {
    if (entry.isDirectory) {
        return false;
    }

    if (entry.isUnsafePath) {
        console.warn(`Skipped zip entry with unsafe path: "${entry.path}"`);
        return false;
    }

    if (entry.isEncrypted) {
        console.warn(`Skipped password protected zip entry: "${entry.path}"`);
        return false;
    }

    if (entry.method !== METHOD_STORED && entry.method !== METHOD_DEFLATE) {
        console.warn(`Skipped zip entry with unsupported compression method ${entry.method}: "${entry.path}"`);
        return false;
    }

    return true;
}

/*
 * The local header repeats the name and extra field with its own lengths, so it has to be read to
 * know where the entry data starts.
 */
async function extractEntry(blob: Blob, entry: TypeZipEntry, rootFolder: string): Promise<CustomFile> {
    const start = entry.localHeaderOffset;
    const header = new DataView(await blob.slice(start, start + SIZE_LOCAL).arrayBuffer());

    if (header.byteLength < SIZE_LOCAL || header.getUint32(0, true) !== SIGNATURE_LOCAL) {
        throw new Error('Invalid local header.');
    }

    const nameLength = header.getUint16(26, true);
    const extraLength = header.getUint16(28, true);
    const dataStart = start + SIZE_LOCAL + nameLength + extraLength;
    const data = blob.slice(dataStart, dataStart + entry.compressedSize);

    const content = entry.method === METHOD_DEFLATE ? await inflateRaw(data) : data;

    /*
     * A truncated or damaged stream still resolves with whatever decoded, so without this a
     * partial file would reach the upload pipeline as if it were intact. The central directory's
     * uncompressed size is the cheap check; throwing here routes the entry to `onError`, which
     * warns and leaves it out.
     */
    if (content.size !== entry.size) {
        throw new Error(`Corrupt entry: expected ${entry.size} bytes, decoded ${content.size}.`);
    }

    const file = new File([content], entry.name, { lastModified: entry.lastModified }) as CustomFile;
    file.relativePath = rootFolder === '' ? entry.path : `${rootFolder}/${entry.path}`;

    return file;
}

async function inflateRaw(data: Blob): Promise<Blob> {
    if (typeof DecompressionStream !== 'function') {
        throw new Error('DecompressionStream is not available in this browser.');
    }

    const stream = data.stream().pipeThrough(new DecompressionStream('deflate-raw'));

    return new Response(stream).blob();
}

function getRootFolder(blob: Blob, rootFolder?: boolean | string): string {
    if (typeof rootFolder === 'string') {
        /*
         * Normalized by the same guard the entry paths go through: the root is prefixed onto every
         * one of them, so a root climbing out of the extraction directory would defeat it. null
         * covers both an unsafe root and an empty one, and either way means "no root folder".
         */
        const path = normalizePath(rootFolder);

        if (path === null && rootFolder !== '') {
            console.warn(`Ignored unsafe zip root folder: "${rootFolder}"`);
        }

        return path ?? '';
    }

    if (rootFolder === false || !(blob instanceof File)) {
        return '';
    }

    // The zip's own name goes through the same guard: a file named "...zip" has ".." as base name.
    return normalizePath(getPath(blob.name).baseName) ?? '';
}

/*
 * Falls back to the default rather than letting a non-numeric value through as NaN, which
 * `runWithConcurrency` would read as zero runners — extract would resolve with an empty list.
 */
function getConcurrency(concurrency?: number): number {
    const parsed = Math.floor(Number(concurrency));

    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 8;
}

// ----------------------------------------------------------------------------

export default extract;
